import React, { useState, useEffect, useRef, useMemo } from "react";
import { Modal, Button, Form, Row, Col, Alert, Select, Input, DatePicker, Table } from "antd";
import { 
  DeleteOutlined, PlusOutlined, DownloadOutlined, BankOutlined,
  EnvironmentOutlined, PhoneOutlined, MailOutlined, UserOutlined,
  IdcardOutlined, CarOutlined, CalendarOutlined, SmileOutlined, ClockCircleOutlined
} from "@ant-design/icons";
import axios from "axios";
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import "./RequestModal.css";

const { Option } = Select;

const RequestModal = ({ show, handleClose }) => {
  const [form] = Form.useForm();
  const [formError, setFormError] = useState("");
  const [success, setSuccess] = useState("");
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceData, setInvoiceData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [mapVisible, setMapVisible] = useState(false);
  const printRef = useRef(null);

  const daysRemaining = useMemo(() => {
    if (!invoiceData?.dueDate) return 0;
    return Math.ceil((new Date(invoiceData.dueDate) - new Date()) / (1000 * 60 * 60 * 24));
  }, [invoiceData?.dueDate]);

  const handleShowMap = () => {
    if (invoiceData?.address) setMapVisible(true);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [settingsRes, productsRes] = await Promise.all([
          axios.get("https://agpipefitting.org.et/api/settings"),
          axios.get("https://agpipefitting.org.et/api/products")
        ]);
        setSettings(settingsRes.data);
        setProducts(productsRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    if (show) fetchData();
  }, [show]);

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const processedData = {
        ...values,
        products: values.products.map(p => ({
          ...p,
          productType: String(p.productType),
          quantity: Number(p.quantity),
          price: Number(p.price),
          unit: products.find(prod => prod.id === Number(p.productType))?.unit || ''
        }))
      };

      const response = await axios.post("https://agpipefitting.org.et/api/purchase-requests", processedData);
      
      const subtotal = processedData.products.reduce((sum, p) => sum + (p.quantity * p.price), 0);
      const vat = subtotal * 0.15;
      
      setInvoiceData({
        ...values,
        products: processedData.products.map(p => ({
          ...p,
          productName: products.find(prod => prod.id === Number(p.productType))?.type || "",
          productCode: products.find(prod => prod.id === Number(p.productType))?.code || ""
        })),
        subtotal: subtotal.toFixed(2),
        vat: vat.toFixed(2),
        totalWithVat: (subtotal + vat).toFixed(2),
        invoiceDate: new Date().toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        }),
        banks: settings?.banks || [],
        invoiceNumber: `INV-${Date.now().toString().slice(-6)}`
      });

      form.resetFields();
      setSuccess("Request submitted successfully!");
      setShowInvoiceModal(true);
      handleClose();
    } catch (err) {
      setFormError(err.response?.data?.message || "Submission failed");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    const input = printRef.current;
    html2canvas(input, { scale: 3 }).then(canvas => {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgProps = pdf.getImageProperties(canvas);
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
      
      pdf.addImage(canvas, 'PNG', 0, 0, pdfWidth, pdfHeight);
      pdf.save(`invoice-${invoiceData?.invoiceNumber}.pdf`);
    });
  };

  return (
    <>
      {/* Request Form Modal */}
      <Modal
        title="New Purchase Requisition"
        open={show}
        onCancel={handleClose}
        footer={null}
        className="request-modal"
        wrapClassName="request-modal-wrapper"
        width="90%"
        style={{ maxWidth: 720 }}
      >
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <div className="form-alerts">
            {formError && <Alert message={formError} type="error" showIcon />}
            {success && <Alert message={success} type="success" showIcon />}
          </div>

          {/* Client Information Section */}
          <div className="form-section">
            <h3><UserOutlined /> Client Information</h3>
            <Row gutter={[16, 8]}>
              <Col xs={24} sm={24} md={12}>
                <Form.Item label="Full Name" name="name" rules={[{ required: true }]}>
                  <Input prefix={<UserOutlined />} size="large" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12}>
                <Form.Item label="Contact Number" name="phone" rules={[{ required: true, pattern: /^[0-9]{10}$/ }]}>
                  <Input prefix={<PhoneOutlined />} size="large" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12}>
                <Form.Item label="Email" name="email" rules={[{ required: true, type: 'email' }]}>
                  <Input prefix={<MailOutlined />} size="large" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12}>
                <Form.Item label="TIN Number" name="tin_no">
                  <Input prefix={<IdcardOutlined />} size="large" />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item label="Delivery Address" name="address" rules={[{ required: true }]}>
                  <Input prefix={<EnvironmentOutlined />} size="large" />
                </Form.Item>
              </Col>
            </Row>
          </div>

          {/* Products Section */}
          <div className="form-section">
            <h3><CarOutlined /> Order Details</h3>
            <Form.List name="products">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }, index) => (
                    <div key={key} className="product-row">
                      <Row gutter={[8, 8]} align="middle">
                        <Col xs={24} sm={24} md={10}>
                          <Form.Item
                            {...restField}
                            name={[name, 'productType']}
                            rules={[{ required: true }]}
                            className="product-select-item"
                          >
                            <Select
                              showSearch
                              placeholder="Select Product"
                              size="large"
                              onChange={value => {
                                const product = products.find(p => p.id === Number(value));
                                form.setFieldsValue({
                                  products: form.getFieldValue('products').map((p, i) => 
                                    i === index ? { 
                                      ...p, 
                                      price: product?.price || 0, 
                                      unit: product?.unit || '' 
                                    } : p
                                  )
                                });
                              }}
                            >
                              {products.map(p => (
                                <Option key={p.id} value={String(p.id)}>
                                  {p.code} - {p.type}
                                </Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col xs={12} sm={8} md={4}>
                          <Form.Item
                            {...restField}
                            name={[name, 'quantity']}
                            rules={[{ required: true, min: 1 }]}
                          >
                            <Input type="number" placeholder="Qty" size="large" />
                          </Form.Item>
                        </Col>
                        <Col xs={12} sm={10} md={6}>
                          <Form.Item
                            {...restField}
                            name={[name, 'price']}
                            rules={[{ required: true, min: 0 }]}
                          >
                            <Input type="number" placeholder="Price" prefix="ETB" size="large" />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={6} md={4} className="delete-col">
                          <div className="delete-button-container">
                            {fields.length > 1 && (
                              <Button 
                                danger 
                                type="link" 
                                onClick={() => remove(name)} 
                                icon={<DeleteOutlined />} 
                                size="large"
                                className="delete-button"
                              />
                            )}
                          </div>
                        </Col>
                      </Row>
                    </div>
                  ))}
                  <Button 
                    type="dashed" 
                    onClick={() => add()} 
                    block 
                    icon={<PlusOutlined />}
                    size="large"
                    className="add-product-btn"
                  >
                    Add Product
                  </Button>
                </>
              )}
            </Form.List>
          </div>

          {/* Delivery Section */}
          <div className="form-section">
            <h3><CalendarOutlined /> Schedule</h3>
            <Row gutter={[16, 8]}>
              <Col xs={24} sm={24} md={12}>
                <Form.Item label="Priority" name="priority" initialValue="Medium">
                  <Select size="large">
                    <Option value="High">Urgent (High Priority)</Option>
                    <Option value="Medium">Standard (Medium Priority)</Option>
                    <Option value="Low">Low Priority</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12}>
                <Form.Item label="Delivery Date" name="due_date" rules={[{ required: true }]}>
                  <DatePicker
                    style={{ width: '100%' }}
                    size="large"
                    disabledDate={current => current && current < Date.now()}
                  />
                </Form.Item>
              </Col>
            </Row>
          </div>

          <Form.Item>
            <Button 
              type="primary" 
              htmlType="submit" 
              block 
              size="large" 
              loading={loading}
              className="submit-btn"
            >
              {loading ? 'Submitting...' : 'Submit Purchase Order'}
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Invoice Modal */}
      <Modal
        open={showInvoiceModal}
        onCancel={() => setShowInvoiceModal(false)}
        footer={[
          <Button key="close" onClick={() => setShowInvoiceModal(false)}>Close</Button>,
          <Button 
            key="download" 
            type="primary" 
            icon={<DownloadOutlined />} 
            onClick={handleDownload}
          >
            Download PDF
          </Button>
        ]}
        className="professional-invoice-modal"
        wrapClassName="invoice-modal-wrapper"
        width="95%"
        style={{ maxWidth: 800 }}
      >
        <div ref={printRef} className="invoice-container">
          {/* Header */}
          <header className="invoice-header">
            <img src="/images/ag3.png" alt="Company Logo" className="company-logo" />
            <Row gutter={[16, 16]} align="middle">
              <Col xs={24} md={14}>
                <div className="company-info">
                  <div className="company-content">
                    <div className="company-details">
                      <div className="company-contact">
                        {settings?.address && (
                          <p>
                            <EnvironmentOutlined /> {settings.address}
                          </p>
                        )}
                        {settings?.phone && (
                          <p>
                            <PhoneOutlined /> {settings.phone}
                          </p>
                        )}
                        {settings?.email && (
                          <p>
                            <MailOutlined /> {settings.email}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </Col>

              <Col xs={24} md={10}>
                <div className="invoice-meta">
                  <h2 className="invoice-title">INVOICE</h2>
                  <div className="invoice-details">
                    <p><span className="invoice-label">Invoice #:</span> {invoiceData?.invoiceNumber}</p>
                    <p><span className="invoice-label">Date:</span> {invoiceData?.invoiceDate}</p>
                  </div>
                </div>
              </Col>
            </Row>
          </header>

          {/* Client & Delivery Info */}
          <Row gutter={[16, 16]} className="info-sections">
            <Col xs={24} md={12}>
              <div className="client-info">
                <h3 className="section-title"><UserOutlined /> Billed To</h3>
                <div className="client-details">
                  <p className="client-name">{invoiceData?.name}</p>
                  {invoiceData?.tin_no && <p><IdcardOutlined /> TIN: {invoiceData.tin_no}</p>}
                  <p><MailOutlined /> {invoiceData?.email}</p>
                </div>
              </div>
            </Col>
            <Col xs={24} md={12}>
              <div className="delivery-info">
                <h3 className="section-title"><CarOutlined /> Delivery Info</h3>
                <div className="delivery-details">
                  <p><EnvironmentOutlined /> {invoiceData?.address}</p>
                  <p><PhoneOutlined /> {invoiceData?.phone}</p>
                </div>
              </div>
            </Col>
          </Row>

          {/* Products Table */}
          <div className="table-responsive">
            <Table
              dataSource={invoiceData?.products}
              columns={[
                { 
                  title: '#', 
                  render: (_, __, index) => index + 1, 
                  width: 50,
                  align: 'center'
                },
                { 
                  title: 'Product', 
                  render: (_, record) => (
                    <div className="product-info">
                      <div className="product-code">{record.productCode}</div>
                      <div className="product-name">{record.productName}</div>
                    </div>
                  )
                },
                { 
                  title: 'Qty', 
                  dataIndex: 'quantity', 
                  width: 80,
                  align: 'center'
                },
                { 
                  title: 'Unit Price', 
                  render: (_, record) => `ETB ${Number(record.price).toLocaleString(undefined, { minimumFractionDigits: 2 })}`,
                  align: 'right',
                  width: 120
                },
                { 
                  title: 'Total', 
                  render: (_, record) => `ETB ${(record.quantity * record.price).toLocaleString(undefined, { minimumFractionDigits: 2 })}`,
                  align: 'right',
                  width: 140
                }
              ]}
              pagination={false}
              className="invoice-table"
              scroll={{ x: true }}
            />
          </div>

          {/* Totals Section */}
          <div className="totals-section">
            <div className="total-row">
              <span className="total-label">Subtotal:</span>
              <span className="total-value">ETB {invoiceData?.subtotal}</span>
            </div>
            <div className="total-row">
              <span className="total-label">VAT (15%):</span>
              <span className="total-value">ETB {invoiceData?.vat}</span>
            </div>
            <div className="total-row grand-total">
              <span className="total-label">Total Due:</span>
              <span className="total-value">ETB {invoiceData?.totalWithVat}</span>
            </div>
          </div>

          {/* Bank Details */}
          {settings?.banks?.length > 0 && (
            <footer className="invoice-footer">
              <div className="bank-details">
                <h5 className="bank-title">
                  <BankOutlined /> Bank Information
                </h5>
                <div className="bank-list">
                  {settings.banks.map((bank, index) => (
                    <div key={index} className="bank-item">
                      <span className="bank-name">{bank.name}:</span>
                      <span className="account-number">{bank.account}</span>
                      {bank.swift && <span className="swift-code">SWIFT: {bank.swift}</span>}
                    </div>
                  ))}
                </div>
              </div>
              <div className="thank-you">
                <SmileOutlined /> Thank you for your business!
              </div>
            </footer>
          )}
        </div>
      </Modal>

      {/* Map Modal */}
      <Modal
        title="Delivery Location"
        open={mapVisible}
        onCancel={() => setMapVisible(false)}
        footer={null}
        className="map-modal"
        width="95%"
        style={{ maxWidth: 600 }}
      >
        <div className="map-preview">
          Map integration placeholder for: {invoiceData?.address}
        </div>
      </Modal>
    </>
  );
};

export default RequestModal;